import React, { useState, useEffect } from 'react';
import { CheckCircle, MessageSquare, ArrowLeft } from 'lucide-react';
import api from '../../api';
import { useFeatures } from '../FeatureGate';

const PlatformFeedback = () => {
  const { isSubEnabled } = useFeatures();
  // Server enforces the same policy (403); this only disables the submit button.
  const canSubmit = isSubEnabled('platform_feedback', 'platform_feedback_submit');
  const [platformForms, setPlatformForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchActiveForms();
  }, []);

  const fetchActiveForms = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/student/lms/platform-feedback/forms');
      setPlatformForms(res.data || []);
    } catch (err) {
      console.error(err);
      setError("Failed to load platform feedback forms. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectForm = (form) => {
    setSelectedForm(form);
    setAnswers(form.answers || {});
    setSuccess(false);
    setError(null);
  };

  const handleBackToList = () => {
    setSelectedForm(null);
    fetchActiveForms();
  };

  const handleAnswerChange = (qId, value) => {
    setAnswers(prev => ({ ...prev, [qId]: value }));
  };

  const isFormComplete = () => {
    if (!selectedForm) return false;
    const questions = selectedForm.questions || [];
    for (const q of questions) {
      let isVisible = true;
      if (q.condition) {
        const { dependsOn, equals } = q.condition;
        const cleanDependsOn = String(dependsOn || '').trim();
        const actualVal = String(answers[cleanDependsOn] || '').trim().toLowerCase();
        const expectedVals = String(equals || '').toLowerCase().split(',').map(s => s.trim());
        if (!expectedVals.includes(actualVal)) isVisible = false;
      }

      if (isVisible) {
        const val = answers[q.id];
        if (val === undefined || val === null || val === '') {
          return false;
        }
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!isFormComplete()) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const payload = {
        course_id: selectedForm.id,
        form_id: selectedForm.id, // platform forms are keyed by their form_type
        course_name: selectedForm.title,
        module_feedback: {},
        platform_feedback: answers
      };

      await api.post('/student/lms/feedback', payload);
      setSuccess(true);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.detail || "Failed to submit feedback.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderQuestion = (q) => {
    const val = answers[q.id] || '';

    // Check condition
    if (q.condition) {
      const { dependsOn, equals } = q.condition;
      const cleanDependsOn = String(dependsOn || '').trim();
      const actualVal = String(answers[cleanDependsOn] || '').trim().toLowerCase();
      const expectedVals = String(equals || '').toLowerCase().split(',').map(s => s.trim());
      if (!expectedVals.includes(actualVal)) return null;
    }

    let inputElement = null;

    if (q.type === 'text') {
      inputElement = (
        <textarea
          value={val || ''}
          onChange={(e) => handleAnswerChange(q.id, e.target.value)}
          placeholder="Type your answer..."
          style={{
            width: '100%',
            minHeight: '100px',
            padding: '12px',
            borderRadius: '8px',
            border: '1px solid var(--border)',
            background: 'var(--bg-main)',
            color: 'var(--text-main)',
            resize: 'vertical',
            boxSizing: 'border-box'
          }}
        />
      );
    } else if (q.type === 'choice') {
      inputElement = (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '10px' }}>
          {q.options?.map((opt, idx) => (
            <label key={idx} style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              cursor: 'pointer',
              padding: '16px',
              borderRadius: '12px',
              border: val === opt ? '2px solid var(--primary)' : '1px solid var(--border)',
              background: val === opt ? 'rgba(14, 165, 233, 0.05)' : 'var(--bg-main)',
              transition: 'all 0.2s ease',
              margin: 0,
              width: '100%',
              boxSizing: 'border-box'
            }}>
              <input
                type="radio"
                name={`pq_${q.id}`}
                value={opt}
                checked={val === opt}
                onChange={() => handleAnswerChange(q.id, opt)}
                style={{
                  margin: '2px 0 0 0',
                  cursor: 'pointer',
                  width: '18px',
                  height: '18px',
                  flexShrink: 0,
                  accentColor: 'var(--primary)'
                }}
              />
              <span style={{
                color: val === opt ? 'var(--primary)' : 'var(--text-main)',
                fontSize: '0.95rem',
                lineHeight: '1.4',
                fontWeight: val === opt ? '600' : '400',
                display: 'block',
                textAlign: 'left'
              }}>
                {opt}
              </span>
            </label>
          ))}
        </div>
      );
    } else if (q.type === 'rating') {
      inputElement = (
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          {[1, 2, 3, 4, 5].map(num => (
            <button
              key={num}
              onClick={() => handleAnswerChange(q.id, num)}
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                border: 'none',
                background: val === num ? 'var(--primary)' : 'var(--bg-main)',
                color: val === num ? '#fff' : 'var(--text-main)',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '1rem',
                boxShadow: val === num ? '0 4px 10px rgba(14, 165, 233, 0.4)' : 'none',
                transition: 'all 0.2s ease'
              }}
            >
              {num}
            </button>
          ))}
          <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginLeft: '10px' }}>
            {val ? `Selected: ${val}` : 'Select 1-5'}
          </span>
        </div>
      );
    }

    return (
      <div key={q.id} style={{ marginBottom: '24px', background: 'var(--bg-card)', padding: '20px', borderRadius: '12px', border: '1px solid var(--border)' }}>
        <h4 style={{ margin: '0 0 12px 0', color: 'var(--text-main)', fontSize: '1.05rem', lineHeight: '1.4', fontWeight: '600' }}>
          {q.text} <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span>
        </h4>
        {inputElement}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
        Loading platform feedback forms...
      </div>
    );
  }

  if (selectedForm && success) {
    return (
      <div style={{ padding: '60px 40px', textAlign: 'center', background: 'var(--bg-card)', borderRadius: '16px', border: '1px solid var(--border)', maxWidth: '600px', margin: '40px auto' }}>
        <CheckCircle size={60} style={{ color: '#10b981', margin: '0 auto 20px' }} />
        <h2 style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--text-main)', marginBottom: '12px' }}>
          Thank you!
        </h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '1.05rem', lineHeight: '1.6', marginBottom: '24px' }}>
          Your feedback for <strong>{selectedForm.title}</strong> has been submitted successfully.
        </p>
        <button
          onClick={handleBackToList}
          style={{
            padding: '10px 24px',
            background: 'var(--primary)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontWeight: '600',
            cursor: 'pointer',
            fontSize: '0.95rem'
          }}
        >
          Back to Feedback Forms
        </button>
      </div>
    );
  }

  return (
    <div className="stu-dashboard-body" style={{ maxWidth: '800px', margin: '0 auto', padding: '20px' }}>
      {!selectedForm ? (
        <div>
          <section className="stu-welcome-section" style={{ marginBottom: '24px' }}>
            <h1 style={{ fontSize: '1.8rem', color: 'var(--text-main)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <MessageSquare size={26} style={{ color: 'var(--primary)' }} /> Platform Feedback
            </h1>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', margin: 0 }}>
              Help us improve your experience by sharing your inputs regarding the platform tools, usability, and speed.
            </p>
          </section>

          {error && (
            <div style={{ padding: '16px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', color: '#b91c1c', marginBottom: '20px', fontSize: '0.95rem' }}>
              {error}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
            {platformForms.map(form => (
              <div
                key={form.id}
                style={{
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border)',
                  borderRadius: '12px',
                  padding: '24px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.02)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  cursor: 'pointer'
                }}
                onClick={() => handleSelectForm(form)}
              >
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <h3 style={{ margin: 0, fontSize: '1.15rem', color: 'var(--text-main)', fontWeight: '700' }}>
                      {form.title}
                    </h3>
                  </div>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: '0 0 20px 0' }}>
                    Questions: {form.questions?.length || 0}
                  </p>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span
                    style={{
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: 700,
                      background: form.submitted ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                      color: form.submitted ? '#10b981' : '#f59e0b'
                    }}
                  >
                    {form.submitted ? 'Submitted' : 'Pending'}
                  </span>
                  <button
                    style={{
                      padding: '8px 16px',
                      background: 'var(--bg-main)',
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      color: 'var(--text-main)',
                      cursor: 'pointer'
                    }}
                  >
                    {form.submitted ? 'View / Edit' : 'Fill Form'}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {platformForms.length === 0 && (
            <div style={{ padding: '40px', background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--border)', textAlign: 'center', color: 'var(--text-muted)' }}>
              No active platform feedback forms are currently configured.
            </div>
          )}
        </div>
      ) : (
        <div>
          <button
            onClick={handleBackToList}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'transparent',
              border: 'none',
              color: 'var(--primary)',
              fontWeight: '600',
              cursor: 'pointer',
              marginBottom: '20px',
              fontSize: '0.95rem',
              padding: 0
            }}
          >
            <ArrowLeft size={16} /> Back to Feedback Forms
          </button>

          <section className="stu-welcome-section" style={{ marginBottom: '24px' }}>
            <h1 style={{ fontSize: '1.8rem', color: 'var(--text-main)', marginBottom: '8px' }}>
              {selectedForm.title}
            </h1>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', margin: 0 }}>
              {selectedForm.submitted ? 'You have already submitted this feedback. You can update your responses below.' : 'Please answer all questions below.'}
            </p>
          </section>

          {error && (
            <div style={{ padding: '16px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', color: '#b91c1c', marginBottom: '20px', fontSize: '0.95rem' }}>
              {error}
            </div>
          )}

          <div>
            {(selectedForm.questions || []).map(renderQuestion)}

            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '15px', marginTop: '30px' }}>
              {!isFormComplete() && !isSubmitting && (
                <span style={{ color: '#ef4444', fontSize: '0.85rem' }}>* Please answer all questions to submit</span>
              )}
              <button
                onClick={handleSubmit}
                disabled={!canSubmit || !isFormComplete() || isSubmitting}
                style={{
                  padding: '12px 30px',
                  background: (!canSubmit || !isFormComplete() || isSubmitting) ? 'var(--text-muted)' : 'var(--primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  fontSize: '1rem',
                  cursor: (!canSubmit || !isFormComplete() || isSubmitting) ? 'not-allowed' : 'pointer',
                  opacity: (!canSubmit || !isFormComplete() || isSubmitting) ? 0.6 : 1,
                  boxShadow: (!canSubmit || !isFormComplete() || isSubmitting) ? 'none' : '0 4px 12px rgba(14, 165, 233, 0.25)',
                  transition: 'all 0.2s ease'
                }}
              >
                {!canSubmit ? 'Submitting disabled' : isSubmitting ? 'Submitting...' : selectedForm.submitted ? 'Update Feedback' : 'Submit Feedback'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformFeedback;
